// Payer Directory
//
// Reads the `payers` Firestore collection (seeded by scripts/import-stedi-payers.js)
// and falls back to the bundled static list when that collection is empty.
//
// The fallback exists because the import needs credentials the app does not have
// at build time, and a PayerPicker with nothing in it is worse than one showing
// the old list. Phase 5 deletes `payersData.ts` once the import is routine.
//
// Static-sourced payers carry no verified Stedi trading partner ID — the payerId
// in that file came from a claims context and may not be what Stedi expects. The
// `source` field is surfaced so the UI can warn instead of silently sending an ID
// the payer will reject.

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { allPayers as staticPayers } from '../data/payersData';
import { Payer, ServiceTypeCode } from '../types/eligibility';

export interface PayerOption {
  id: string;
  stediPayerId: string;
  name: string;
  address?: string;
  isMbho: boolean;
  optimalServiceTypeCode?: ServiceTypeCode;
  /** 'static' means the trading partner ID is unverified — see note above. */
  source: 'stedi' | 'static' | 'manual';
}

let cache: PayerOption[] | null = null;
let inFlight: Promise<PayerOption[]> | null = null;

function fromFirestore(payer: Payer): PayerOption {
  return {
    id: payer.id,
    stediPayerId: payer.stediPayerId,
    name: payer.displayName || payer.name,
    address: payer.address,
    isMbho: Boolean(payer.isMbho),
    optimalServiceTypeCode: payer.optimalServiceTypeCode,
    source: payer.source || 'stedi',
  };
}

function fromStatic(): PayerOption[] {
  const seen = new Set<string>();

  return staticPayers
    .filter((payer) => {
      const key = String(payer.payerId).toLowerCase();
      if (!payer.payerId || !payer.name || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((payer) => ({
      id: `static-${payer.payerId}`,
      stediPayerId: String(payer.payerId),
      name: payer.name,
      address: payer.address,
      isMbho: false,
      source: 'static' as const,
    }));
}

export class PayerDirectoryService {
  /**
   * All selectable payers, Firestore first.
   * Concurrent callers share one request — PayerPicker mounts in several places.
   */
  static async list(): Promise<PayerOption[]> {
    if (cache) return cache;
    if (inFlight) return inFlight;

    inFlight = (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'payers'), where('isActive', '==', true))
        );

        const payers = snap.docs.map((doc) => fromFirestore({ id: doc.id, ...doc.data() } as Payer));
        cache = payers.length ? payers : fromStatic();
      } catch (error) {
        // A rules or network failure should degrade to the static list rather
        // than leave the picker empty and the front desk stuck.
        console.warn('[PayerDirectory] Falling back to static payer list:', error);
        cache = fromStatic();
      } finally {
        inFlight = null;
      }

      return cache;
    })();

    return inFlight;
  }

  /**
   * Match on name, trading partner ID, or address.
   * Exact ID matches and name-prefix matches sort first — typing "aetna" should
   * not bury Aetna under every plan that mentions it in an address.
   */
  static async search(term: string, limit = 50): Promise<PayerOption[]> {
    const payers = await this.list();
    const needle = term.trim().toLowerCase();

    if (!needle) {
      return [...payers].sort((a, b) => a.name.localeCompare(b.name)).slice(0, limit);
    }

    const scored = payers
      .map((payer) => {
        const name = payer.name.toLowerCase();
        const id = payer.stediPayerId.toLowerCase();

        let score = 0;
        if (id === needle) score = 100;
        else if (name === needle) score = 90;
        else if (name.startsWith(needle)) score = 70;
        else if (id.startsWith(needle)) score = 60;
        else if (name.includes(needle)) score = 40;
        else if ((payer.address || '').toLowerCase().includes(needle)) score = 10;

        return { payer, score };
      })
      .filter((entry) => entry.score > 0);

    scored.sort((a, b) => b.score - a.score || a.payer.name.localeCompare(b.payer.name));
    return scored.slice(0, limit).map((entry) => entry.payer);
  }

  static async findById(payerId: string): Promise<PayerOption | undefined> {
    return (await this.list()).find((payer) => payer.id === payerId);
  }

  /** Drop the cache — call after running the payer import. */
  static clearCache(): void {
    cache = null;
    inFlight = null;
  }
}
